import { useContext, useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useFormik } from 'formik';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames';
import * as Yup from 'yup';
import { autop } from '@wordpress/autop';

import API from '@utils/api';
import PageContext from '@contexts/PageContext';
import { getLanguageId } from '@helpers/language';
import { getBackupImg } from '@helpers/dataExists';
import ToastShow from '@services/toastService';
import { Container, Button, CheckboxInput } from '@components';
import { checkEnabled, checkValue } from '@components/OptionalFeature';
import Collapse from './Collapse';

import styles from './BodyContent.module.scss';
import ContactStyles from './ContactForm.module.scss';

const DynamicImage = dynamic(() => import('@components/DynamicImage'), { ssr: false });

const contactForm = ['FirstName', 'LastName', 'EmailAddress', 'phone', '', 'ZipOrPostalCode', 'model', 'serial', 'Comments'];
const emailRegExp = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const BodyContent = ({ data, pageId, componentIsAvailable, showTitle, componentTitle }) => {
  const router = useRouter();
  const { locale } = router;
  const pageData = useContext(PageContext);
  const { PostText, PostTextSection, DisplayOptions } = pageData;
  const [isSubmit, setIsSubmit] = useState(false);

  // check FormElements
  const isForm = DisplayOptions?.find((item) => item.Name === 'FormElements')?.DisplayOptions;

  // check FormElements is enable
  const formElement = isForm?.filter((item) => item.Enabled);
  // contact form get sequence
  const sequenceForm = contactForm?.reduce((acc, val) => {
    let finObj = formElement?.find((f) => f.DisplayOptionKey == val);
    finObj && acc.push(finObj);
    return acc;
  }, []);

  const filteredPostTextSection = PostTextSection?.filter((post) => parseInt(post.LanguageId) === getLanguageId(locale));

  // check button target is Enable
  const buttonTargetAvailable = checkEnabled(data?.DisplayOptions, 'ButtonTarget');

  // check button text is Enabled
  const buttonTextAvailable = checkEnabled(data?.DisplayOptions, 'ButtonText');

  // check button url is enabled
  const buttonURLAvailable = checkEnabled(data?.DisplayOptions, 'ButtonURL');

  // check has image
  const hasImage = checkEnabled(data?.DisplayOptions, 'HasImage');

  // check Title is enabled
  const titleIsAvailable = checkEnabled(data?.DisplayOptions, 'Title');

  // check SectionBody is Enabled
  const sectionBodyAvailable = checkEnabled(data?.DisplayOptions, 'SectionBody');

  // check Collapse is Enabled
  const collapseEnabled = checkEnabled(DisplayOptions?.find((item) => item.Name == 'Body Individual')?.DisplayOptions, 'Collapse');

  // button text
  const buttonText = checkValue(isForm, 'ButtonText', locale);

  useEffect(async () => {
    await getBackupImg();
  });

  const formSchema = Yup.object().shape({
    FirstName: Yup.string().required('Please enter your first name'),
    LastName: Yup.string().required('Please enter your last name'),
    EmailAddress: Yup.string().matches(emailRegExp, 'Invalid email address').required('Please enter your email'),
    Comments: Yup.string().required('Please enter your comment'),
    // model: Yup.string().required("Please enter your model"),
    ZipOrPostalCode: Yup.string().required('Please enter your Postal Code'),
    // serial: Yup.string().required("Please enter your Serial number"),
    // isReceivingInfo: Yup.boolean().oneOf([true], 'Must Accept commercial information '),
  });

  const formik = useFormik({
    initialValues: {
      FirstName: '',
      LastName: '',
      EmailAddress: '',
      model: '',
      serial: '',
      ZipOrPostalCode: '',
      phone: '',
      Comments: '',
      isReceivingInfo: false
    },
    validationSchema: formSchema,
    validateOnBlur: true,
    onSubmit: async (values, actions) => {
      let showToast = ToastShow();
      let data = {
        type: 'FormData',
        fieldValues: [
          {
            type: 'FieldValue',
            id: '220',
            name: 'Email Address',
            value: values?.EmailAddress?.toString()
          },
          {
            type: 'FieldValue',
            name: 'First Name',
            id: '221',
            value: values?.FirstName?.toString()
          },
          {
            type: 'FieldValue',
            name: 'Last Name',
            id: '222',
            value: values?.LastName?.toString()
          },
          {
            type: 'FieldValue',
            name: 'Zip or Postal Code',
            id: '226',
            value: values?.ZipOrPostalCode?.toString()
          },
          {
            type: 'FieldValue',
            name: 'Comments',
            id: '292',
            value: values?.Comments?.toString()
          }
          // {
          //   type: 'FieldValue',
          //   name: 'Model',
          //   id: '486',
          //   value: values?.model.toString()
          // },
          // {
          //   type: 'FieldValue',
          //   name: 'serial',
          //   id: '761', // ID not found in new eloqua form ids.
          //   value: values?.serial.toString()
          // }
        ]
      };
      API.post('/api/contactForm', data).then(
        (res) => {
          if (res['id']) {
            formik.resetForm();
            
              router.push('/contact-success');
            
          } else {
            showToast.error('Error check into field value');
          }
        },
        (err) => {
          showToast.error('Error check into field value');
        }
      );
      
    }
  });

  // comment error msg
  const commentErrorText = checkValue(isForm, 'CommentsMissingText', locale);

  // email error msg
  const emailErrorText = checkValue(isForm, 'EmailAddressFormatErrorText', locale);

  // firstName msg
  const firstNameErrorText = checkValue(isForm, 'FirstNameMissingText', locale);

  // general error msg
  const generalErrorText = checkValue(isForm, 'GeneralFormErrorText', locale);

  // lastName error text
  const lastNameErrorText = checkValue(isForm, 'LastNameMissingText', locale);

  // zipcode error text
  const zipCodeErrorText = checkValue(isForm, 'ZipOrPostalCodeMissingText', locale);

  const emailoptin = checkValue(isForm, 'EmailOpt-In', locale);

  return (
    componentIsAvailable &&
    PostTextSection.length > 0 ? (
      <section className={pageId === 27 ? styles.Left : styles.Section}>
        <Container className={styles.Container}>
          {showTitle && <h2 className={styles.ComponentTitle}>{componentTitle}</h2>}
          {filteredPostTextSection.map((post, index) => (
            <div className={styles.BoxBorder} key={index}>
              {collapseEnabled ? (
                <>
                  <Collapse
                    title={post.Title}
                    isTitleAvailable={titleIsAvailable}
                    children={
                      post.HasImage || (sectionBodyAvailable && post.SectionBody) ?
                        <>
                          {post.HasImage && (
                            <DynamicImage
                              imgSrc={`/api/static/media/byFile/PostTextSections/${post.Id}/PostTextSection/1_N_N_N_I_640x360.jpg`}
                              sizes={'640x360'}
                              alt={post.Title}
                              className={styles.Image}
                            />
                          )}
                          {sectionBodyAvailable && post.SectionBody && (
                            <div
                              className={styles.Table}
                              dangerouslySetInnerHTML={{ __html: autop(post.SectionBody) }}
                            />
                          )}
                        </>
                      : null
                    }
                  />
                </>
              ) : (
                <>
                  {post.HasImage && (
                    <DynamicImage
                      imgSrc={`/api/static/media/byFile/PostTextSections/${post.Id}/PostTextSection/1_N_N_N_I_640x360.jpg`}
                      sizes={'640x360'}
                      alt={post.Title}
                      className={styles.Image}
                    />
                  )}
                  {titleIsAvailable && <h2 className={styles.Title}>{post.Title}</h2>}
                  {sectionBodyAvailable && post.SectionBody && (
                    <div
                      className={styles.Table}
                      dangerouslySetInnerHTML={{ __html: autop(post.SectionBody) }}
                    />
                  )}
                </>
              )}
              {buttonTextAvailable && post.ButtonText != null && post.ButtonText != '' && (
                <Button
                  animated
                  className={styles.Button}
                  href={buttonURLAvailable ? post.ButtonUrl : null}
                  target={buttonTargetAvailable ? post.ButtonTarget : null}
                >
                  {post.ButtonText}
                </Button>
              )}
            </div>
          ))}
          {sequenceForm && sequenceForm?.length > 0 && (
            <section className={ContactStyles.contactDealer}>
              <div className={ContactStyles.noteContainer}>
                <h2 className={classNames(ContactStyles.notePadding)}>
                  Have a specific question for Kubota Canada?
                </h2>

                <div className={ContactStyles.formContainer}>
                  <form onSubmit={formik.handleSubmit}>
                    {sequenceForm?.map((m) => {
                      let label = m?.OptionInformation;
                      return (
                        <div className={ContactStyles.contactDealerForm}>
                          <label>{label[locale]}</label>
                          {m?.DisplayOptionKey !== 'Comments' && (
                            <div className={ContactStyles.formInputContainer}>
                              <input
                                placeholder={`${label[locale]}`}
                                type="text"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                id={m?.DisplayOptionKey}
                                name={m?.DisplayOptionKey}
                                value={formik.values[m?.DisplayOptionKey]}
                              />
                              {!formik.errors[m?.DisplayOptionKey] && formik.touched[m?.DisplayOptionKey] && (
                                <div className={ContactStyles.formValid}>
                                  <FontAwesomeIcon icon={faCheckCircle} />
                                </div>
                              )}
                            </div>
                          )}
                          {/* <p>{formik.values[m?.DisplayOptionKey]}</p> */}
                          {m?.DisplayOptionKey == 'Comments' && (
                            <textarea
                              placeholder={`${label[locale]}`}
                              type="text"
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              id={m?.DisplayOptionKey}
                              name={m?.DisplayOptionKey}
                              value={formik.values[m?.DisplayOptionKey]}
                              rows="6"
                            ></textarea>
                          )}
                          {/* {formik.errors[m?.DisplayOptionKey] && formik.touched[m?.DisplayOptionKey] && (
                            <div className={ContactStyles.formError}>{commentErrorText}</div>
                          )} */}
                           {formik.errors[m?.DisplayOptionKey] && (
                              <div className={ContactStyles.formError}>
                                {m?.DisplayOptionKey == 'FirstName'
                                  ? `${firstNameErrorText}`
                                  : m?.DisplayOptionKey == 'LastName'
                                  ? `${lastNameErrorText}`
                                  : m?.DisplayOptionKey == 'ZipOrPostalCode'
                                  ? `${zipCodeErrorText}`
                                  : m?.DisplayOptionKey == 'EmailAddress'
                                  ? `${emailErrorText}`
                                  :m?.DisplayOptionKey == 'Comments'
                                  ? `${commentErrorText}`
                                  : null}
                              </div>
                            )}
                        </div>
                      );
                    })}

                    <div className={`${ContactStyles.contactDealerForm} ${ContactStyles.terms}`}>
                      <div className={ContactStyles.selectBox}>
                        <CheckboxInput
                          title={emailoptin}
                          className={ContactStyles.Compare}
                          onChange={formik.handleChange}
                          name="isReceivingInfo"
                          value={formik.values.isReceivingInfo}
                          id="isReceivingInfo"
                          // disabled={checkedProducts.length >= 3 ? true : false}
                          checked={formik.values.isReceivingInfo}
                        />
                      </div>
                      {formik.errors?.isReceivingInfo && (
                        <div className={ContactStyles.formError}>{formik.errors?.isReceivingInfo}</div>
                      )}
                    </div>

                    <div className={`${ContactStyles.formRow} ${ContactStyles.submitRow}`}>
                      <button
                        className={`${ContactStyles.button} ${ContactStyles.buttonSubmit} ${ContactStyles.smaller} ${ContactStyles.onWhite} ${
                          isSubmit && ContactStyles.disabled
                        }`}
                        type="submit"
                      >
                        {isSubmit && <div className="loader"></div>}
                        {buttonText}
                      </button>
                      {formik.values.FirstName !== '' &&
                      formik.values.LastName !== '' &&
                      formik.values.EmailAddress !== '' &&
                      formik.values.ZipOrPostalCode !== '' ? null : (
                        <div className={ContactStyles.formError}>{generalErrorText}</div>
                      )}
                    </div>
                  </form>
                </div>
              </div>
            </section>
          )}
        </Container>
      </section>
    ) : null
  );
};

export default BodyContent;
